import React, { useMemo } from 'react';
import { ITEM_ROW_HEIGHT, DataListRenderer, ListHead, ItemRow } from '../ListView';
import { ListDisplayTypes } from '../../constants';
import { getStyle } from '../../ui/styling';
import { useSearchApiContext, FILTERS_BAR_HEIGHT } from './contexts';

/* The list's sticky header slot. The shadcn toolbar (ListSearchControls) renders inside it; the
   slot itself, and in table mode the column header row, stay legacy here (UND-1133), as they were
   inside the toolbar before. */
const useStickyHeaderSlot = (renderHeader, isTableDisplay) =>
  useMemo(() => {
    if (!renderHeader) {
      return renderHeader;
    }
    return (headerProps) => {
      const { key, height, style } = headerProps;
      const toolbarHeight = height - FILTERS_BAR_HEIGHT - (isTableDisplay ? ITEM_ROW_HEIGHT : 0);
      return (
        <ListHead key={key} {...getStyle(style)} bg="$white" height={toolbarHeight} zIndex={1200}>
          {renderHeader({ ...headerProps, height: undefined, style: undefined })}
          {isTableDisplay ? (
            <ItemRow
              head
              height={ITEM_ROW_HEIGHT}
              position="sticky"
              top={height - FILTERS_BAR_HEIGHT - ITEM_ROW_HEIGHT - 4 /* <- 4 for preventing hairline gaps */}
              zIndex="$header"
              bg="$white"
            />
          ) : null}
        </ListHead>
      );
    };
  }, [renderHeader, isTableDisplay]);

export const ListSearchDataRenderer = React.forwardRef((props, ref) => {
  const { styles, tableItemComponent, cardItemComponent, renderHeader, ...rest } = props;
  const { data, displayType = ListDisplayTypes.CARDS, loading, requestMoreData, requestingMore } = useSearchApiContext();
  const renderHeaderInSlot = useStickyHeaderSlot(renderHeader, displayType === ListDisplayTypes.TABLE);

  const Component = displayType === ListDisplayTypes.TABLE ? tableItemComponent : cardItemComponent;

  return (
    <DataListRenderer
      ref={ref}
      data={data}
      displayType={displayType}
      loading={loading}
      requestMoreData={requestMoreData}
      requestingMore={requestingMore}
      itemComponent={Component}
      renderHeader={renderHeaderInSlot}
      styles={[
        {
          root: {
            minHeight: 400,
            padTop: ({ theme }) => theme.sizes.appBarHeight + theme.spacing(1),
          },
          item: {
            height: displayType === ListDisplayTypes.CARDS ? ({ theme }) => theme.breakpoints({ xs: 312, sm: 186 }) : ITEM_ROW_HEIGHT,
            mb: displayType === ListDisplayTypes.CARDS ? '$3' : 0,
          },
          listLoading: {
            justifyContent: 'center',
          },
          header: {
            height: ({ theme }) =>
              theme.breakpoints({
                xs: 186 + theme.sizes.appBarHeight + theme.spacing(1),
                sm: 154 + theme.sizes.appBarHeight + FILTERS_BAR_HEIGHT + (displayType === ListDisplayTypes.TABLE ? ITEM_ROW_HEIGHT : 0),
              }),
          },
        },
        styles,
      ]}
      {...rest}
    />
  );
});
